/**
 * 从自然语言解析建提醒意图(无 LLM、无 iris)。
 * P0 轻量规则:覆盖「N分钟后提醒我喝水」「每天10点…」等常见说法。
 * P1:规则未命中且像设提醒时,前端改走 POST /pet/reminders/parse(LLM)。
 */
import type { CreateReminderBody } from '@/api/petReminders'

const TZ = 'Asia/Shanghai'

/** 明确的「设提醒」动词 —— 单独出现基本就是要设提醒。 */
const REMINDER_VERB = /提醒|叫我|喊我|闹钟|定时/u
/** 时间信号:循环 / 相对 / 具体时刻。 */
const TIME_SIGNAL =
  /每天|每周|每月|每隔|分钟后|小时后|钟头后|明天|后天|大后天|今晚|早上|上午|中午|下午|傍晚|晚上|凌晨|半夜|\d\s*[:：]\s*\d|\d+\s*点/u
/** 弱信号:太常出现在闲聊里,必须叠加时间信号才算像设提醒。 */
const WEAK_HINT = /记得|别忘|到点/u

/**
 * 粗判是否像「设提醒」—— 命中才值得打 LLM parse(省额度/延迟)。
 * 明确动词直接算;弱词(记得/别忘/到点)须叠加时间信号,避免「我每天都好累」
 * 这类闲聊白白触发一次计费解析。
 */
export function looksLikeReminderIntent(text: string): boolean {
  const t = text.trim()
  if (REMINDER_VERB.test(t)) return true
  return TIME_SIGNAL.test(t) && WEAK_HINT.test(t)
}

const ZH_DIGIT: Record<string, number> = {
  零: 0,
  一: 1,
  二: 2,
  两: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9
}

/** 解析「一 / 15 / 二十」等为正整数(不支持小数)。 */
export function parseZhNumber(raw: string): number | null {
  const s = raw.trim()
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10)
    return n > 0 ? n : null
  }
  if (s === '十') return 10
  if (s.length === 2 && s[0] === '十' && s[1] in ZH_DIGIT) return 10 + ZH_DIGIT[s[1]]
  if (s.length === 2 && s[1] === '十' && s[0] in ZH_DIGIT) return ZH_DIGIT[s[0]] * 10
  if (s.length === 3 && s[1] === '十' && s[0] in ZH_DIGIT && s[2] in ZH_DIGIT) {
    return ZH_DIGIT[s[0]] * 10 + ZH_DIGIT[s[2]]
  }
  if (s in ZH_DIGIT) return ZH_DIGIT[s]
  return null
}

function cleanTitle(s: string): string {
  return s
    .trim()
    .replace(/^[：:，,\s]+/, '')
    .replace(/[。！!~～?？]+$/, '')
}

function atFromNow(ms: number): CreateReminderBody {
  return {
    title: '',
    kind: 'at',
    at_ts: new Date(Date.now() + ms).toISOString(),
    tz: TZ
  }
}

/**
 * 匹配则返回建提醒 body;否则 null(走普通聊天)。
 */
export function tryParseReminderIntent(text: string): CreateReminderBody | null {
  const t = text.trim().replace(/[？?。！!~～\s]+$/g, '')

  // N(分钟|分)后[提醒我]xxx
  let m = t.match(
    /^(\d+|[一二两三四五六七八九十]{1,3})(?:个)?(?:分钟|分)(?:钟)?(?:之?后|以?后)?(?:再)?(?:提醒(?:我)?|叫我|喊我)?(.+)$/u
  )
  if (m) {
    const n = parseZhNumber(m[1])
    const title = cleanTitle(m[2])
    if (n && n >= 1 && title) {
      return { ...atFromNow(n * 60_000), title }
    }
  }

  // N小时[后][提醒我]xxx
  m = t.match(
    /^(\d+|[一二两三四五六七八九十]{1,3})(?:个)?(?:小时|钟头)(?:之?后|以?后)?(?:再)?(?:提醒(?:我)?|叫我|喊我)?(.+)$/u
  )
  if (m) {
    const n = parseZhNumber(m[1])
    const title = cleanTitle(m[2])
    if (n && n >= 1 && title) {
      return { ...atFromNow(n * 3_600_000), title }
    }
  }

  // 每天H点[分][提醒我]xxx
  m = t.match(/^每天(\d{1,2})点(?:(\d{1,2})分)?(?:半)?(?:提醒(?:我)?|叫我)?(.+)$/u)
  if (m) {
    const hh = parseInt(m[1], 10)
    const mm = m[2] ? parseInt(m[2], 10) : m[0].includes('半') ? 30 : 0
    const title = cleanTitle(m[3])
    if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59 && title) {
      return { title, kind: 'cron', cron_expr: `${mm} ${hh} * * *`, tz: TZ }
    }
  }

  // 明天H点[分][提醒我]xxx
  m = t.match(/^明天(\d{1,2})点(?:(\d{1,2})分)?(?:半)?(?:提醒(?:我)?|叫我)?(.+)$/u)
  if (m) {
    const hh = parseInt(m[1], 10)
    const mm = m[2] ? parseInt(m[2], 10) : m[0].includes('半') ? 30 : 0
    const title = cleanTitle(m[3])
    if (hh >= 0 && hh <= 23 && title) {
      const d = new Date()
      d.setDate(d.getDate() + 1)
      d.setHours(hh, mm, 0, 0)
      return { title, kind: 'at', at_ts: d.toISOString(), tz: TZ }
    }
  }

  return null
}

/** 确认话术里用的简短时间描述。 */
export function describeParsedReminder(body: CreateReminderBody): string {
  if (body.kind === 'at' && body.at_ts) {
    const target = new Date(body.at_ts).getTime()
    const diffMin = Math.round((target - Date.now()) / 60_000)
    if (diffMin > 0 && diffMin < 24 * 60) {
      if (diffMin < 60) return `${diffMin} 分钟后`
      const h = Math.floor(diffMin / 60)
      const r = diffMin % 60
      return r ? `${h} 小时 ${r} 分钟后` : `${h} 小时后`
    }
    const d = new Date(body.at_ts)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  }
  if (body.kind === 'cron' && body.cron_expr) {
    const p = body.cron_expr.split(/\s+/)
    if (p.length === 5 && p[2] === '*' && p[3] === '*') {
      const t = `${p[1].padStart(2, '0')}:${p[0].padStart(2, '0')}`
      if (p[4] === '*') return `每天 ${t}`
      const days = ['日', '一', '二', '三', '四', '五', '六']
      const d = Number(p[4])
      if (d >= 0 && d <= 6) return `每周${days[d]} ${t}`
    }
  }
  if (body.kind === 'every' && body.every_ms) {
    const h = body.every_ms / 3_600_000
    if (h >= 1 && Number.isInteger(h)) return `每 ${h} 小时`
    const m = body.every_ms / 60_000
    if (m >= 1) return `每 ${m} 分钟`
  }
  return '到点'
}
